import { FieldLabel } from "../workspaces/shared/FieldLabel";
import { useEffect, useState } from "react";
import { userMessage } from "../workspaces/shared/userMessage";
import { PrivacyNotice } from "./PrivacyNotice";
import { AuthLayout } from "./AuthLayout";
import { Callout } from "../workspaces/shared/ui";
import { CheckCircle2, MailCheck } from "lucide-react";

/**
 * INVITATION, PASSWORD RESET AND "FORGOT PASSWORD" (Phase 13B, §58A/§58B).
 *
 * An emailed link is the only way an account becomes usable — an admin's
 * invitation, or (spec §86) the confirmation link a person sent themselves by
 * signing up — and the person, never an admin, chooses the password here.
 */

const MIN_LENGTH = 8;

/** Spec §88A: every link-from-email screen uses the front-door layout. */
function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return <AuthLayout title={title}>{children}</AuthLayout>;
}

function PasswordFields({
  password,
  confirm,
  onPassword,
  onConfirm,
}: {
  password: string;
  confirm: string;
  onPassword: (value: string) => void;
  onConfirm: (value: string) => void;
}) {
  return (
    <>
      <FieldLabel>
        Choose a password
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => onPassword(event.target.value)}
          required
          minLength={MIN_LENGTH}
          autoFocus
        />
        <span className="auth-hint">At least {MIN_LENGTH} characters. A short phrase works well.</span>
      </FieldLabel>
      <FieldLabel>
        Type it again
        <input
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => onConfirm(event.target.value)}
          required
        />
      </FieldLabel>
    </>
  );
}

/** Accept an invitation: /invite?token=… */
export function AcceptInvitationScreen({ token, onActivated }: { token: string; onActivated: () => void }) {
  const [state, setState] = useState<{ checked: boolean; valid: boolean; email: string }>({
    checked: false,
    valid: false,
    email: "",
  });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Spec §86: a self-service signup agrees to the terms before the account exists.
  const selfSignup = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("signup") === "1";
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch(`/api/invitation?token=${encodeURIComponent(token)}`);
        const payload = (await response.json()) as { valid: boolean; email?: string };
        setState({ checked: true, valid: payload.valid, email: payload.email ?? "" });
      } catch {
        setState({ checked: true, valid: false, email: "" });
      }
    })();
  }, [token]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setError("Those two passwords are different.");
      return;
    }
    setBusy(true);
    try {
      const response = await fetch("/api/invitation/accept", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, acceptTerms: selfSignup ? acceptTerms : true }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string; signedIn?: boolean };
      if (!response.ok) throw new Error(payload.error ?? "That did not work.");
      setError(null);
      if (payload.signedIn) {
        // The server signed them in: straight into the app, where first-login
        // onboarding picks up.
        window.location.href = "/";
        return;
      }
      setDone(true);
    } catch (err) {
      setError(userMessage(err, "That did not work."));
    } finally {
      setBusy(false);
    }
  };

  if (!state.checked) {
    return (
      <Shell title="Checking your invitation…">
        <p className="muted">One moment.</p>
      </Shell>
    );
  }

  if (!state.valid) {
    return (
      <Shell title="This link has expired">
        <p className="muted workspace-note">
          {selfSignup
            ? "Confirmation links work once and expire after seven days. Sign up again with the same address and a fresh link is sent."
            : "Invitation links work once and expire after seven days. Contact the Berrier Reno administrator at Berrier Holdings for a new invitation — it takes them a few seconds to send one."}
        </p>
        <button className="btn" onClick={onActivated}>
          Go to sign in
        </button>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell title="Your account is ready">
        <div className="auth-success">
          <span className="auth-success-icon" aria-hidden="true">
            <CheckCircle2 size={26} />
          </span>
          <p className="auth-lede">Sign in with {state.email} and the password you just chose.</p>
        </div>
        <button className="btn btn-primary" onClick={onActivated}>
          Sign in
        </button>
      </Shell>
    );
  }

  return (
    <Shell title="Welcome to Berrier Reno">
      <p className="auth-lede">
        Setting up <strong>{state.email}</strong>. Choose a password and you are in — your projects are private to your account.
      </p>
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <PasswordFields password={password} confirm={confirm} onPassword={setPassword} onConfirm={setConfirm} />
        {selfSignup && (
          <FieldLabel className="checkbox-label">
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} required />
            <span>
              I agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and{" "}
              <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </span>
          </FieldLabel>
        )}
        {error && <Callout tone="bad">{error}</Callout>}
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? "Setting up…" : "Create my account"}
        </button>
      </form>
      <PrivacyNotice variant="onboarding" />
    </Shell>
  );
}

/** Choose a new password: /reset?token=… */
export function ResetPasswordScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const [state, setState] = useState<{ checked: boolean; valid: boolean; email: string }>({
    checked: false,
    valid: false,
    email: "",
  });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch(`/api/password/reset?token=${encodeURIComponent(token)}`);
        const payload = (await response.json()) as { valid: boolean; email?: string };
        setState({ checked: true, valid: payload.valid, email: payload.email ?? "" });
      } catch {
        setState({ checked: true, valid: false, email: "" });
      }
    })();
  }, [token]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setError("Those two passwords are different.");
      return;
    }
    setBusy(true);
    try {
      const response = await fetch("/api/password/reset", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(payload.error ?? "That did not work.");
      setDone(true);
      setError(null);
    } catch (err) {
      setError(userMessage(err, "That did not work."));
    } finally {
      setBusy(false);
    }
  };

  if (!state.checked) {
    return (
      <Shell title="Checking that link…">
        <p className="muted">One moment.</p>
      </Shell>
    );
  }

  if (!state.valid) {
    return (
      <Shell title="That reset link is no longer valid">
        <p className="muted workspace-note">
          Reset links work once and expire after an hour. Use <strong>Forgot password?</strong> on the sign-in page to
          get another one.
        </p>
        <button className="btn" onClick={onDone}>
          Back to sign in
        </button>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell title="Password changed">
        <p className="muted workspace-note">
          Sign in with your new password. Any other session you had open has been signed out.
        </p>
        <button className="btn btn-primary" onClick={onDone}>
          Sign in
        </button>
      </Shell>
    );
  }

  return (
    <Shell title="Choose a new password">
      <p className="muted workspace-note">For {state.email}.</p>
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <PasswordFields password={password} confirm={confirm} onPassword={setPassword} onConfirm={setConfirm} />
        {error && <Callout tone="bad">{error}</Callout>}
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Change my password"}
        </button>
      </form>
    </Shell>
  );
}

/** "Forgot password?" — deliberately says the same thing either way. */
export function ForgotPasswordPanel({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await fetch("/api/password/forgot", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch {
      /* the answer below is the same either way */
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <Shell title="Check your email">
        <div className="auth-success">
          <span className="auth-success-icon" aria-hidden="true">
            <MailCheck size={26} />
          </span>
          <p className="auth-lede">If that address has a Berrier Reno account, a reset link is on its way. It works once and expires in an hour.</p>
        </div>
        <button className="btn" onClick={onBack}>
          Back to sign in
        </button>
      </Shell>
    );
  }

  return (
    <Shell title="Reset your password">
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <p className="auth-lede">Enter the email you sign in with and we will send a link to choose a new password.</p>
        <FieldLabel>
          Email
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoFocus
          />
        </FieldLabel>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? "Sending…" : "Send a reset link"}
        </button>
        <button className="link-button" type="button" onClick={onBack}>
          Back to sign in
        </button>
      </form>
    </Shell>
  );
}
